import { ReactElement, useCallback, useId, useRef, useState } from 'react'
import { Button, IconButton, ListItemIcon, Menu, MenuItem, SxProps, Tooltip } from '@mui/material'

import { SQUARE_ICON_BUTTON_SX } from '@/common/styles'

type BaseMenuOption = {
  text: string
  Icon?: ReactElement
  disabled?: boolean
}

/**
 * An option either performs an action (`onClick`) or is an external link (`href`).
 * Link options render as real anchors so they can be opened in a new tab/window.
 */
export type MenuOption =
  | (BaseMenuOption & { onClick: () => void; href?: never })
  | (BaseMenuOption & { href: string; onClick?: never })

export interface ButtonMenuProps {
  label: string
  options: MenuOption[]
  includeText?: boolean
  Icon: ReactElement
  square?: boolean
  sx?: SxProps
  useTooltip?: boolean
  /**
   * Put the open menu inside the `<header>` that holds the button, not at the end of
   * `<body>`. A menu at the end of `<body>` is outside every landmark (axe `region`);
   * inside the page header it is part of the `banner` landmark.
   *
   * Do not use `disablePortal` for this. The menu's container would stay `<body>`, and
   * MUI sets `aria-hidden` on every child of the container except the menu's own root.
   * With `disablePortal` that hides the app root, and the open menu is inside it.
   */
  portalIntoHeader?: boolean
}

export const ButtonMenu = ({
  label,
  options,
  includeText = false,
  Icon,
  square = false,
  sx,
  useTooltip = true,
  portalIntoHeader = false,
}: ButtonMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const id = useId()

  const wrapperRef = useRef<HTMLDivElement>(null)
  // Must give the same answer for the whole life of the menu, closing included. MUI
  // re-reads the container whenever this function changes, and moving the menu while it
  // closes remounts its exit transition, which then never reports that it has finished.
  // MUI then keeps the menu's invisible full-page backdrop, and the page takes no clicks.
  // So read the wrapper (always mounted), not `anchorEl` (null once the menu closes), and
  // keep the function itself stable.
  const getHeaderContainer = useCallback(() => wrapperRef.current?.closest('header') ?? null, [])

  const commonProps = {
    id,
    'aria-controls': open ? `${id}-menu` : undefined,
    'aria-haspopup': true,
    'aria-expanded': open,
    onClick: handleClick,
  }

  const IconButtonComponent = (
    <IconButton
      aria-label={label}
      sx={square ? [SQUARE_ICON_BUTTON_SX, ...(Array.isArray(sx) ? sx : [sx])] : sx}
      {...commonProps}
    >
      {Icon}
    </IconButton>
  )

  const anchor = includeText ? (
    <Button variant="contained" size="large" {...commonProps} startIcon={Icon} sx={sx}>
      {label}
    </Button>
  ) : useTooltip ? (
    <Tooltip title={label}>{IconButtonComponent}</Tooltip>
  ) : (
    IconButtonComponent
  )

  return (
    <div ref={wrapperRef}>
      {anchor}
      <Menu
        id={`${id}-menu`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        // `null` (no header found) makes MUI fall back to `<body>`.
        container={portalIntoHeader ? getHeaderContainer : undefined}
        slotProps={{
          list: {
            'aria-labelledby': id,
          },
        }}
      >
        {options.map(option => (
          <MenuItem
            key={option.text}
            disabled={option.disabled || false}
            {...('href' in option
              ? {
                  component: 'a',
                  href: option.href,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                  onClick: handleClose,
                  sx: {
                    color: 'inherit',
                    // The global `a:hover` rule is more specific than the MenuItem class, so it
                    // has to be overridden explicitly for link options to match action options.
                    '&:hover': { color: 'inherit' },
                  },
                }
              : {
                  // Close first. An action that opens a dialog would otherwise leave the
                  // menu, and its invisible backdrop, open behind the dialog.
                  onClick: () => {
                    handleClose()
                    option.onClick()
                  },
                })}
          >
            {option.Icon && <ListItemIcon>{option.Icon}</ListItemIcon>}
            {option.text}
          </MenuItem>
        ))}
      </Menu>
    </div>
  )
}
